import type { QecListEvent } from '@avisoma/shared';
import { CrossedOutCell } from './CrossedOutCell';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedContentTd, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type QecRow = QecListEvent & { pageId?: string };

/**
 * Dense grid for `qecListEvents` (QEC / LRU accessory list rows, DR-2024).
 * Used on both the entity view (consolidated extraction, with a source-page
 * column) and the page view (raw per-page rows, no page column — pass no
 * `pageIdToPosition`). Rarely-present fields (tso, dateInstalled, category,
 * statusRaw) live in the row inspector; statusRaw also surfaces as the
 * status badge's tooltip.
 */
export function QECTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: QecRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: QecRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>#</Th>
          <Th>Description</Th>
          <Th>OEM PN</Th>
          <Th>Vendor PN</Th>
          <Th>Serial</Th>
          <Th>Status</Th>
          <Th>Qty</Th>
          <Th>Pos</Th>
          <Th>IPC Ref</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>Date</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId ?? 'page'}-${r.rowIndex}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="text-gray-500 dark:text-zinc-400">{r.rowIndex}</Td>
            <TruncatedContentTd title={r.description} maxWidth="max-w-[280px]">
              <CrossedOutCell text={r.description || '—'} crossedOut={r.crossedOut} truncate />
            </TruncatedContentTd>
            <Td className="font-mono">{r.oemPartNumber ?? '—'}</Td>
            <Td className="font-mono">{r.vendorPartNumber ?? '—'}</Td>
            <Td className="font-mono">{r.serialNumber ?? '—'}</Td>
            <Td>
              <StatusBadge status={r.status} statusRaw={r.statusRaw} />
            </Td>
            <Td>{r.quantity ?? '—'}</Td>
            <Td>{r.position ?? '—'}</Td>
            <Td className="font-mono">{r.ipcReference ?? '—'}</Td>
            <Td>{r.tsn ?? '—'}</Td>
            <Td>{r.csn ?? '—'}</Td>
            <Td title={r.dateInstalled ? `Installed: ${r.dateInstalled}` : undefined}>
              {r.date ?? r.dateInstalled ?? '—'}
            </Td>
            <TruncatedTd value={r.remarks} maxWidth="max-w-[240px]" />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({
  status,
  statusRaw,
}: {
  status: QecListEvent['status'];
  statusRaw: string | undefined;
}) {
  if (!status) {
    return statusRaw ? (
      <span className="text-gray-500 dark:text-zinc-400">{statusRaw}</span>
    ) : (
      <>—</>
    );
  }
  const tone =
    status === 'Installed'
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
      : status === 'Not Supplied' || status === 'Not Installed'
        ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200'
        : status === 'Not Visible'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200'
          : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200';
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${tone}`}
      title={statusRaw && statusRaw !== status ? `Raw: ${statusRaw}` : undefined}
    >
      {status}
    </span>
  );
}
